import Box from "@mui/material/Box";
import { Autocomplete, TextField, Typography } from "@mui/material";
import { useRef, useMemo } from "react";
import dayjs from "dayjs";
import { SLASH_FORMAT_DATE } from "constants/format";
import LabelHeader from "components/Label/LabelHeader";
import TextFieldComponent, { TextFieldRef } from "components/input/TextFieldComponent";
import { ValidateOption } from "hooks/useValidateInput";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { MESSAGE } from "constants/validate";
import { getUserStorage } from "util/local_storage";
import { useNavigate } from "react-router";
import ButtonComponent from "components/button/ButtonComponent";
import { HEADER_EST_RESULT, HEADER_EST_SUB_RESULT } from "constants/data";
import Notify from "config/notify";
import CardCarModel from "features/car_model/components/CardCarModel";
import WheeItemRow from "features/mst_proc_fee/components/WheelItemRow";
import QuotationResultTableTire from "features/mst_proc_fee/components/QuotationResultTableTire";
import ServiceCostSummary from "features/mst_proc_fee/components/ServiceCostSummary";
import "dayjs/locale/ja";
import useProcFeeConfirm from "features/mst_proc_fee/hooks/useProcFeeConfirm";
import useCreateWheelQuote from "features/mst_proc_fee/hooks/useCreateWheelQuote";
import useScrollToError from "hooks/useScrollToError";

interface InputHeaderControlProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  validateOption?: ValidateOption;
  onError?: (error: boolean, message: string) => void;
  required?: boolean;
}

/**
 * CalendarIconComponent renders a calendar icon image.
 * This component is used as a custom icon for date pickers.
 */
function CalendarIcon() {
  return <img src="/img/icons/calendar.svg" alt="calendar-icon" style={{ padding: "0px" }} />;
}

/**
 * InputHeaderControl is a component that displays an input field with a label above it
 * @param props - Component props including:
 * @param props.label - Label displayed above the input field
 * @param props.value - Value of the input field
 * @param props.onChange - Callback function when value changes
 * @param props.validateOption - Validation options for the input field
 * @param props.onError - Callback function when validation errors occur
 * @param props.required - Whether the field is required
 * @returns JSX.Element
 */
function InputHeaderControl({
  label,
  value,
  onChange,
  validateOption,
  onError,
  required,
}: InputHeaderControlProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: { xs: "100%", lg: "209px" } }}>
      <LabelHeader
        required={required}
        label={label}
        sxTypography={{ ml: 0 }}
        sxBox={{
          justifyContent: "center",
          border: "1px solid var(--primary-500)",
          backgroundColor: "var(--blue-400)",
        }}
      />
      <TextFieldComponent
        value={value}
        sxInput={{
          textAlign: "center",
          fontSize: { xs: "16px", md: "18px", xl: "20px" },
          height: "50px",
        }}
        sxRoot={{ borderRadius: 0 }}
        onChangeText={onChange}
        validateOption={validateOption}
        onError={onError}
      />
    </Box>
  );
}

/**
 * ProcFeeConfirm is a component that displays a quote confirmation form with customer information,
 * store details, and product selection.
 *
 * 見積金額（タイヤ行の confirm_total_price）のキャッシュバック控除は useProcFeeConfirm の再計算と
 * priceCalculator.calculateCashback に集約: キャッシュバックチェック ON かつ 期間中（cashback_period === 2）のみ控除。
 * 見積作成 API の cb_tire_price / cb_wheel_price は useCreateWheelQuote で常に送信し、
 * チェック OFF または 期間外のときは 0、チェック ON かつ 期間中のときは実額。
 *
 * @returns JSX.Element
 */
function ProcFeeConfirm() {
  const navigate = useNavigate();
  const user = getUserStorage();
  const customerNameRef = useRef<TextFieldRef>(null);
  const customerNameContainerRef = useRef<HTMLDivElement>(null);
  const scrollToCustomerName = useScrollToError(customerNameContainerRef);

  const {
    wheelOptionsPrice,
    customerInfo,
    costSetting,
    quoteByList,
    selectOptions,
    newQuoteMaker,
    wheelTireCombinations,
    hasNonStandardQuantity,
    setCustomerInfo,
    setCostSetting,
    updateWheelTireDetail,
    updateWheelRemarks,
    handleSelectAllTires,
    handleClearAllTires,
  } = useProcFeeConfirm();

  // Sort for display only (does not affect API payload).
  // Each entry keeps originalIndex (wheel) and originalTireIndices (per tire) so update
  // callbacks use the correct store indices even after sorting.
  // Wheel groups: higher final_price (ホイール4本合計) first.
  // Tire rows within each group: higher confirm_total_price (見積金額 set total) first.
  const sortedWheelTireCombinations = useMemo(
    () =>
      wheelTireCombinations
        .map((combination, originalIndex) => {
          const tiresWithIdx = combination.tires.map((tire, tireIdx) => ({
            tire,
            originalTireIndex: tireIdx,
          }));
          const sortedTiresWithIdx = [...tiresWithIdx].sort(
            (a, b) => (b.tire.confirm_total_price ?? 0) - (a.tire.confirm_total_price ?? 0),
          );
          return {
            ...combination,
            originalIndex,
            tires: sortedTiresWithIdx.map(({ tire }) => tire),
            originalTireIndices: sortedTiresWithIdx.map(({ originalTireIndex }) => originalTireIndex),
          };
        })
        .sort((a, b) => (b.wheel.final_price ?? 0) - (a.wheel.final_price ?? 0)),
    [wheelTireCombinations],
  );

  const { isSubmitting, handleCreateWheelQuote } = useCreateWheelQuote({
    customerInfo,
    costSetting,
    wheelTireCombinations,
    newQuoteMaker,
    selectOptions,
    wheelOptionsPrice,
  });

  const handleSubmit = async () => {
    // Manual validation
    const hasError = customerNameRef.current?.validate();
    if (hasError) {
      scrollToCustomerName();
      return;
    }

    // Validate other requirements
    if (wheelTireCombinations.length === 0) {
      Notify.setNotification({
        show: true,
        title: "商品を選択してください",
        actionsPosition: "center",
      });
      return;
    }

    const hasCheckedItems = wheelTireCombinations.some((combination) =>
      combination.tires.some((tire) => tire.checked),
    );

    if (!hasCheckedItems) {
      Notify.setNotification({
        show: true,
        title: "商品を選択してください",
        actionsPosition: "center",
      });
      return;
    }

    // if (!newQuoteMaker) {
    //   Notify.setNotification({
    //     show: true,
    //     title: "車両情報が見つかりません",
    //     actionsPosition: "center",
    //   });
    //   return;
    // }

    // Call API
    await handleCreateWheelQuote();
  };

  const SUB_HEADER_RESULT = [
    ...HEADER_EST_SUB_RESULT.slice(0, -1),
    {
      label: (
        <Typography
          component="span"
          sx={{ fontWeight: "bold", fontSize: { xs: "16px", md: "18px", xl: "20px" } }}
        >
          見積金額 <br />{" "}
          <Typography sx={{ fontWeight: "inherit", fontSize: 10 }}>
            (タイヤホイールセット)
          </Typography>
        </Typography>
      ),
      sx: { backgroundColor: "var(--red-500)" },
    },
  ];

  return (
    <>
      {/* Quote Header Section */}
      <Box
        sx={{
          p: { xs: 2, md: 2.5, lg: 5 },
          display: "flex",
          justifyContent: { xs: "center", xl: "space-between" },
          flexDirection: { xs: "column", xl: "row" },
          alignItems: { xs: "center", xl: "flex-start" },
          gap: { xs: 4, xl: "auto" },
        }}
      >
        {/* Customer Information Section */}
        <Box
          sx={{
            display: "flex",
            width: { xs: "100%", md: "fit-content", xl: "auto" },
            flexDirection: "column",
            gap: "9px",
          }}
        >
          {/* Quote Title and Customer Details */}
          <Box sx={{ display: "flex", gap: 7, mb: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: "180px" }}>
              <Typography
                sx={{
                  fontSize: "31px",
                  color: "var(--gray-700)",
                  fontWeight: "medium",
                  textAlign: "center",
                }}
              >
                お見積表
              </Typography>
              <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, textAlign: "center" }}>
                {dayjs().format(SLASH_FORMAT_DATE)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: "3px", flex: 1 }}>
              {/* Customer Name Input */}
              <Box ref={customerNameContainerRef} sx={{ width: { xs: "100%", lg: "209px" } }}>
                <LabelHeader
                  required
                  label="お客様氏名"
                  sxTypography={{ ml: 0 }}
                  sxBox={{
                    justifyContent: "center",
                    border: "1px solid var(--primary-500)",
                    backgroundColor: "var(--blue-400)",
                  }}
                />
                <TextFieldComponent
                  ref={customerNameRef}
                  value={customerInfo.customer_name}
                  sxInput={{
                    textAlign: "center",
                    fontSize: { xs: "16px", md: "18px", xl: "20px" },
                    height: "50px",
                  }}
                  sxRoot={{ borderRadius: 0 }}
                  onChangeText={(value) => {
                    setCustomerInfo({ customer_name: value });
                  }}
                  validateOption={{
                    required: {
                      message: MESSAGE.EMPTY,
                    },
                    maxLength: {
                      message: MESSAGE.MAX_LENGTH(100),
                      value: 100,
                    },
                  }}
                />
              </Box>

              {/* Phone Number Input */}
              <InputHeaderControl
                label="電話番号"
                value={customerInfo.customer_tel_no}
                onChange={(value) => setCustomerInfo({ customer_tel_no: value })}
                validateOption={{
                  includeNumberOnly: { message: MESSAGE.INCLUDE_NUMBER_ONLY },
                  halfWidth: { message: MESSAGE.HALFWIDTH },
                  minLength: { message: MESSAGE.MIN_LENGTH(10), value: 10 },
                  maxLength: { message: MESSAGE.MAX_LENGTH(11), value: 11 },
                }}
              />
            </Box>
          </Box>

          {/* Store Information Section */}
          <Box sx={{ display: "flex", gap: 7 }}>
            <Typography
              sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, textAlign: "center", width: "180px" }}
            >
              {user?.storeName}
            </Typography>
            <Box sx={{ display: "flex", flex: 1, gap: "3px" }}>
              {/* Staff Selection */}
              <Box sx={{ width: { xs: "100%", xl: "209px" } }}>
                <LabelHeader
                  label="担当者"
                  sxTypography={{ ml: 0 }}
                  sxBox={{ justifyContent: "center" }}
                />
                <Autocomplete
                  disablePortal
                  options={quoteByList}
                  sx={{
                    border: "1px solid var(--offwhite-250)",
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      py: "3px",
                      borderRadius: "0px",
                      width: "100%",
                    },
                    "& .MuiOutlinedInput-input": {
                      textAlign: "center",
                      fontSize: { xs: "16px", md: "18px", xl: "20px" },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid var(--gray-250)",
                    },
                  }}
                  renderInput={(inputParams) => <TextField {...inputParams} label="" />}
                  inputValue={customerInfo.quote_by}
                  value={customerInfo.quote_by ? { label: customerInfo.quote_by } : null}
                  noOptionsText="該当するデータが見つかりません"
                  onInputChange={(_, value) => {
                    setCustomerInfo({ quote_by: value });
                  }}
                />
              </Box>

              {/* Expiration Date Selection */}
              <Box sx={{ width: { xs: "100%", xl: "209px" } }}>
                <LabelHeader
                  label="有効期限"
                  sxTypography={{ ml: 0 }}
                  sxBox={{ justifyContent: "center" }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                  <DatePicker
                    label=""
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                      minWidth: "unset !important",
                      "& .MuiInputBase-root": {
                        borderRadius: 0,
                        border: 0,
                      },
                      "& .MuiOutlinedInput-input": {
                        textAlign: "center",
                        py: "10.5px",
                        fontSize: { xs: "16px", md: "18px", xl: "20px" },
                      },
                      "& .MuiButtonBase-root": {
                        padding: "0px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "1px solid var(--gray-250)",
                      },
                    }}
                    value={dayjs(customerInfo.exp_date)}
                    slots={{ openPickerIcon: CalendarIcon }}
                    format={SLASH_FORMAT_DATE}
                    onChange={(newValue) => {
                      setCustomerInfo({ exp_date: newValue?.toISOString() ?? "" });
                    }}
                    minDate={dayjs(new Date())}
                    desktopModeMediaQuery="@media (min-width: 0px)"
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Car Model Card */}
        <CardCarModel
          backRoute={() => navigate(-1)}
          isFromCarType
          newQuoteMaker={newQuoteMaker}
          sx={{ justifyContent: "center", width: { xs: "100%", md: "fit-content", xl: "auto" } }}
          tireSizeText={newQuoteMaker?.selectedTire?.mmm_car_tiresize ?? ""}
          showRimDiameterField
        />
      </Box>

      {/* Quote Details Section */}
      <Box
        sx={{
          px: { xs: 2, md: 2.5, lg: 5 },
          display: "flex",
          backgroundColor: "var(--offwhite-100)",
          py: 3,
          justifyContent: "center",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: { xs: "flex-start", lg: "space-between" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", xl: "column" },
              justifyContent: "end",
              gap: 1,
              alignItems: { xs: "center", xl: "start" },
            }}
          >
            <ButtonComponent
              theme="dark"
              label="選択した商品で見積確定"
              sx={{ minWidth: "fit-content", whiteSpace: "nowrap" }}
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
            <Box display="flex" gap={1}>
              <ButtonComponent
                label="全選択"
                sx={{
                  minWidth: "fit-content",
                  height: 31,
                  width: 95,
                  fontSize: 17,
                  borderRadius: 26,
                }}
                onClick={handleSelectAllTires}
              />
              <ButtonComponent
                label="全クリア"
                sx={{
                  minWidth: "fit-content",
                  height: 31,
                  width: 95,
                  fontSize: 17,
                  borderRadius: 26,
                }}
                onClick={handleClearAllTires}
              />
            </Box>
          </Box>
          <Box
            sx={{
              overflowX: "auto",
              width: { xs: "100%", lg: "auto" },
            }}
          >
            <ServiceCostSummary
              type="confirm"
              costSettingInfo={costSetting}
              setCostSettingInfo={setCostSetting}
              selectOptions={selectOptions}
              disablePunctureAndCashback={hasNonStandardQuantity}
              hideValve
              sxBox={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                minWidth: "1370px",
              }}
            />
          </Box>
        </Box>

        {sortedWheelTireCombinations.length > 0 &&
          sortedWheelTireCombinations.map((combination, displayIndex) => (
            <Box
              key={`wheel-${combination.wheel.item_cd}-${combination.wheel.id}`}
              sx={{
                overflowX: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                py: "2px",
                width: "100%",
                boxSizing: "border-box",
                border: "4px solid #BFBFBF",
                mb: displayIndex < sortedWheelTireCombinations.length - 1 ? 2 : 0,
              }}
            >
              <WheeItemRow
                wheel={combination.wheel}
                optionsPrice={wheelOptionsPrice}
                onRemarkChange={(remarks) => updateWheelRemarks(combination.originalIndex, remarks)}
              />
              <QuotationResultTableTire
                mainHeader={HEADER_EST_RESULT}
                subHeader={SUB_HEADER_RESULT}
                rounding={Number(costSetting.rounding)}
                quoteDetailTire={combination.tires}
                updateQuotationTire={(key, sortedTireIndex, value) =>
                  updateWheelTireDetail(
                    combination.originalIndex,
                    combination.originalTireIndices[sortedTireIndex] ?? sortedTireIndex,
                    key,
                    value,
                  )
                }
                wheelFinalPrice={combination.wheel?.final_price ?? 0}
              />
            </Box>
          ))}
      </Box>
    </>
  );
}

export default ProcFeeConfirm;
